/**
 * Space Worm — dealing a board (specs/space-worm-mode.md §7).
 *
 * A board is one word from the picture pool and a scatter of tiles: one tile per step of the word,
 * plus the rung's decoys. Nothing here is language-specific — the word, its steps, its Korean
 * blocks and its Thai clusters all come from `cat/word.ts`, which is why this mode costs no new
 * content in any of the ten languages.
 *
 * The interesting half is **placement**, and all of it is about fairness rather than solvability:
 * the board wraps (§5.2), so every cell is reachable from every other and there is no maze to
 * verify. What there is instead is a worm that is already moving — after a wrong bite the board is
 * re-dealt around a head that is mid-flight (§5.4) — so every rule below is measured from wherever
 * that head happens to be, and never from a start cell the dealer would have preferred.
 *
 * A board is allowed to be hard to READ. It is never allowed to be hard to REACH: a book here costs
 * the whole word, so a tile lost to geometry is a word lost to geometry.
 *
 * Phaser-free.
 */
import type { Pack } from '@/data/packs/types';
import { WORM } from '@/game/config';
import { wordCandidates, type DoorWord } from '@/game/cat/word';
import { matches, formsOf, type LetterClass } from '@/game/cat/membership';
import { cellsOf } from '@/game/maze/segment';
import { dist, beadPositions, type Pt, type Bounds, type Worm } from './step';
import type { WormBoard, WormTile } from './order';

type Rng = () => number;

export interface WormDealInput {
  /** 1-based rung of the ladder (§4.4) — decides word length, decoy count and speed. */
  rung: number;
  classes: LetterClass[];
  /** Letter classes the child has met (`cat/store.ts`). */
  metIds: Set<string>;
  /** Classes they have cleared a board with — the second tier of the decoy pool. */
  knownIds: Set<string>;
  lang: string;
  picturePool: Pack | null;
  /** Word ids this run has already used, finished or lost. */
  usedWordIds: Set<string>;
  /** The worm as it is RIGHT NOW — the placer works around it, never the other way (§7.4). */
  worm: Worm;
  bounds: Bounds;
  rng: Rng;
}

function shuffled<T>(xs: T[], rng: Rng): T[] {
  const out = xs.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * The word for a rung: the pool's candidates, nearest to the length the rung asked for.
 *
 * The ladder wants 3 · 4 · 4 · 5 · 5 steps, but a language does not owe us a word of exactly that
 * length — Thai clusters and Korean jamo count differently, and a run excludes what it has already
 * used. So the fallback is "nearest length", never "fail": a rung that cannot deal is the one bug
 * that ends a run with nothing on the screen.
 */
export function pickWordForRung(
  pool: Pack | null,
  classes: LetterClass[],
  metIds: Set<string>,
  lang: string,
  rung: number,
  used: Set<string>,
  rng: Rng,
): DoorWord | null {
  const want = WORM.steps[Math.min(rung, WORM.steps.length) - 1];
  // Met letters first — a Worm run drills the alphabet the Cat runs introduced. If the store is
  // cold (a child who reached star 13 without ever spending a Cat), every class counts as met
  // rather than the mode refusing to deal: this treat sits at the top of the ladder and its player
  // can read.
  const allIds = new Set(classes.map((c) => c.id));
  let candidates = wordCandidates(pool, classes, metIds, lang, used);
  if (candidates.length === 0) candidates = wordCandidates(pool, classes, allIds, lang, used);
  // Everything has been used this run: allow repeats rather than end the run early.
  if (candidates.length === 0) candidates = wordCandidates(pool, classes, allIds, lang, new Set());
  if (candidates.length === 0) return null;

  const best = Math.min(...candidates.map((c) => Math.abs(c.steps.length - want)));
  const shortlist = candidates.filter((c) => Math.abs(c.steps.length - want) === best);
  return shortlist[Math.floor(rng() * shortlist.length)];
}

/**
 * Decoy glyphs: letters that are **not in this word at all** (§7.3).
 *
 * Never "a letter of this word, out of turn" — those are already on the board and already end the
 * word. Drawn from the targets' own `decoyPreferClasses` first, which is what makes a wrong bite a
 * real misread (`b`/`d`, `ㅁ`/`ㅇ`, `ก`/`ถ`) rather than a slip.
 *
 * Only authored forms are used, never the generated ones: in Korean the steps are jamo, and
 * `formsOf` would offer whole syllable blocks for a board that has none.
 */
export function decoyGlyphs(
  word: DoorWord,
  classes: LetterClass[],
  knownIds: Set<string>,
  lang: string,
  n: number,
  rng: Rng,
): string[] {
  if (n <= 0) return [];
  const stepSet = new Set(word.steps);
  const targetClasses = classes.filter((c) => word.steps.some((s) => matches(s, c)));
  const targetIds = new Set(targetClasses.map((c) => c.id));
  const preferred = new Set(targetClasses.flatMap((c) => c.prefer));

  const tier = (c: LetterClass): number => {
    if (preferred.has(c.id)) return 0;
    if (knownIds.has(c.id)) return 1;
    return 2;
  };

  // A capital among lowercase letters is "the one odd-looking cell", which is a cue about SHAPE
  // that has nothing to do with the letter — so a decoy prefers the case the word is written in.
  // A preference, not a filter: scripts without case have to be able to fill a board too.
  const wordIsLower = word.steps.every((g) => g === g.toLowerCase());
  const caseMatches = (g: string): boolean => (g === g.toLowerCase()) === wordIsLower;

  const pool: { glyph: string; tier: number }[] = [];
  for (const c of classes) {
    if (targetIds.has(c.id)) continue;
    const forms = word.composes ? c.forms : formsOf(c, lang);
    for (const g of forms) {
      // One cell per tile, always — a two-cluster "letter" would not fit a cell and would read as
      // two letters to the child.
      if (cellsOf(g, lang).length !== 1) continue;
      if (stepSet.has(g)) continue;
      if (targetClasses.some((t) => matches(g, t))) continue;
      pool.push({ glyph: g, tier: tier(c) * 2 + (caseMatches(g) ? 0 : 1) });
    }
  }
  const seen = new Set<string>();
  const ordered = shuffled(pool, rng)
    .sort((a, b) => a.tier - b.tier)
    .filter((p) => (seen.has(p.glyph) ? false : (seen.add(p.glyph), true)));
  return ordered.slice(0, n).map((p) => p.glyph);
}

/**
 * Is this point clear of the worm — its body, the space around its head, and the runway it is
 * about to fly down (§7.4)?
 *
 * The runway is a **corridor**, not a row of cells: everything within `runwayHalfWidth` of the line
 * the head is currently travelling, for `runwayPx` ahead of it. A worm that can turn at any angle
 * needs the space it is actually heading into kept clear, and that space is a strip.
 *
 * Measured in a straight line — the board is a rectangle. A worm that is off the edge entirely
 * (§5.2) has its runway pointing at nothing, which costs the placer a corridor it did not need to
 * keep clear and no correctness at all.
 */
export function clearOfWorm(p: Pt, worm: Worm, bounds: Bounds): boolean {
  if (dist(p, worm.head, bounds) < WORM.headClear) return false;
  for (const b of beadPositions(worm, WORM.beadSpacing, bounds)) {
    if (dist(p, b, bounds) < WORM.minTileGap * 0.7) return false;
  }
  // Ahead of the head, in its own frame: forward distance and sideways offset.
  const d = { x: p.x - worm.head.x, y: p.y - worm.head.y };
  const forward = d.x * Math.cos(worm.angle) + d.y * Math.sin(worm.angle);
  const sideways = -d.x * Math.sin(worm.angle) + d.y * Math.cos(worm.angle);
  if (forward > 0 && forward < WORM.runwayPx && Math.abs(sideways) < WORM.runwayHalfWidth) return false;
  return true;
}

/**
 * Scatter `count` tiles, no two of them closer than `minTileGap`, all of them clear of the worm.
 *
 * Rejection sampling rather than a grid walk, because there is no grid — and because the spacing
 * rule is the whole approach-lane rule (§7.4) in one number: a letter far enough from every other
 * letter can always be driven at from somewhere.
 *
 * Returns null when it cannot; the caller drops a decoy and tries again. Dropping a decoy is always
 * safe, dropping a target never is.
 */
export function placeTiles(
  count: number, worm: Worm, bounds: Bounds, rng: Rng,
): Pt[] | null {
  const placed: Pt[] = [];
  for (let tries = 0; tries < WORM.placeTries && placed.length < count; tries++) {
    // Inset, so no letter is ever drawn half off the board. The worm wraps; the letters do not.
    const inset = WORM.edgeInset;
    const p = {
      x: bounds.x + inset + rng() * (bounds.w - inset * 2),
      y: bounds.y + inset + rng() * (bounds.h - inset * 2),
    };
    if (!clearOfWorm(p, worm, bounds)) continue;
    if (placed.some((q) => dist(p, q, bounds) < WORM.minTileGap)) continue;
    placed.push(p);
  }
  return placed.length >= count ? placed : null;
}

/**
 * Deal one board around a worm that may be anywhere, pointing anywhere, and moving.
 *
 * Returns null only when the picture pool itself has nothing to offer — the scene treats that as
 * "end the run kindly", never as a crash.
 */
export function dealWormBoard(input: WormDealInput): WormBoard | null {
  const { rung, classes, metIds, knownIds, lang, picturePool, usedWordIds, worm, bounds, rng } = input;
  const word = pickWordForRung(picturePool, classes, metIds, lang, rung, usedWordIds, rng);
  if (!word) return null;

  const wantDecoys = WORM.decoys[Math.min(rung, WORM.decoys.length) - 1];

  let decoys = decoyGlyphs(word, classes, knownIds, lang, wantDecoys, rng);
  let spots = placeTiles(word.steps.length + decoys.length, worm, bounds, rng);
  while (spots === null && decoys.length > 0) {
    decoys = decoys.slice(0, -1);
    spots = placeTiles(word.steps.length + decoys.length, worm, bounds, rng);
  }
  // The targets alone will not fit. Nothing a run can reach produces this, but a board missing a
  // letter must never be returned.
  if (spots === null) return null;

  const glyphs = [...word.steps.map((g) => ({ glyph: g, target: true })),
    ...decoys.map((g) => ({ glyph: g, target: false }))];
  // Targets take a shuffled set of the chosen spots, so the word's first letter is not reliably the
  // nearest tile to the worm.
  const order = shuffled(spots, rng);
  const tiles: WormTile[] = glyphs.map((g, i) => ({
    id: i, glyph: g.glyph, x: order[i].x, y: order[i].y, target: g.target,
  }));

  return { rung, word, tiles, eaten: 0 };
}
